import moment, { isMoment } from "moment-timezone";
/*
  Types
*/
export var AvailableNowOptions;
(function (AvailableNowOptions) {
    /**
     * Return the normal formatted date string.
     */
    AvailableNowOptions[AvailableNowOptions["NotAvailableNow"] = 1] = "NotAvailableNow";
    /**
     * Always return the text `"Available Now"`.
     */
    AvailableNowOptions[AvailableNowOptions["IsAvailableNow"] = 2] = "IsAvailableNow";
    /**
     * Return the text `"Available Now"` only if the date is before today.
     */
    AvailableNowOptions[AvailableNowOptions["Calculate"] = 3] = "Calculate";
})(AvailableNowOptions || (AvailableNowOptions = {}));
/*
  Constants
*/
/**
 * Company preferred date formatting pattern. Set per moment docs:
 * https://momentjs.com/docs/#/displaying/format/
 *
 * Ex: 2025 Jun 7
 */
export const AUTOSLED_DATE_FORMAT = "YYYY MMM D";
/**
 * Company preferred time formatting pattern. Set per moment docs.
 *
 * Ex: 12:07 PM
 */
export const AUTOSLED_TIME_FORMAT = "hh:mm A";
/**
 * Combined date + time formatting pattern. Set per moment docs.
 *
 * Ex: 2025 Jun 7, 12:07 PM
 */
export const AUTOSLED_DATE_FORMAT_WITH_TIME = `${AUTOSLED_DATE_FORMAT}, ${AUTOSLED_TIME_FORMAT}`;
const DEFAULT_LOCALE = "en";
const DEFAULT_DATE_FORMAT_OPTIONS = {
    showTime: false,
    timeOnly: false,
    availableNowModifier: AvailableNowOptions.NotAvailableNow,
    toTimeZone: undefined,
    toLocale: DEFAULT_LOCALE,
};
/*
  Formatting functions
*/
/**
 * Formats a Moment date using company-standard formatting and rules.
 */
export function dateToStandardFormat(date, options = DEFAULT_DATE_FORMAT_OPTIONS) {
    if (!date || !isMoment(date))
        return "";
    const dateCopy = moment.utc(date);
    if (!dateCopy.isValid())
        return "";
    const { showTime, timeOnly, availableNowModifier, toTimeZone, toLocale } = options;
    /*
      Resolve "Available Now" modifier
    */
    if (availableNowModifier === AvailableNowOptions.IsAvailableNow)
        return "Available Now";
    if (availableNowModifier === AvailableNowOptions.Calculate) {
        const now = moment.utc();
        if (dateCopy.isBefore(now)) {
            return "Available Now";
        }
    }
    /*
      Resolve locale
    */
    const selectedLocale = toLocale ?? DEFAULT_LOCALE;
    dateCopy.locale(selectedLocale);
    /*
      Resolve time conversion
    */
    if (!toTimeZone) {
        dateCopy.local();
    }
    else {
        // Ensure string passed is valid per moment docs: https://momentjs.com/timezone/docs/#/data-loading/checking-if-a-zone-exists/
        const isValidIANATimeZone = moment.tz.zone(toTimeZone);
        if (isValidIANATimeZone) {
            dateCopy.tz(toTimeZone);
        }
    }
    /*
      Resolve format
    */
    let selectedFormat;
    if (showTime && timeOnly) {
        selectedFormat = AUTOSLED_TIME_FORMAT;
    }
    else if (showTime) {
        selectedFormat = AUTOSLED_DATE_FORMAT_WITH_TIME;
    }
    else {
        selectedFormat = AUTOSLED_DATE_FORMAT;
    }
    return dateCopy.format(selectedFormat);
}
/**
 * Formats an ISO 8601 date string.
 * @param dateString
 */
export function dateStringToStandardFormat(dateString, options) {
    if (!dateString)
        return "";
    return dateToStandardFormat(moment.utc(dateString), options);
}
